import React from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { headingToId } from "../../../utils/docs/slugify";
import { DocsCallout } from "../ui/DocsCallout";
import { DocsAccordion, DocsAccordionItem } from "../ui/DocsAccordion";
import { DocsStepByStep, Step } from "../ui/DocsStepByStep";
import { DocsImageWithCaption } from "../ui/DocsImageWithCaption";
import { DocsVideoEmbed } from "../ui/DocsVideoEmbed";
import { DocsSlackButton } from "../ui/DocsSlackButton";
import { cn } from "../../../lib/utils";

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

// Split content by custom components and return array of parts
function splitByCustomComponents(
  content: string,
): Array<{ type: "markdown" | "component"; content: string; props?: any }> {
  const parts: Array<{
    type: "markdown" | "component";
    content: string;
    props?: Record<string, unknown>;
  }> = [];
  let lastIndex = 0;

  // Match all custom components
  const patterns = [
    {
      regex: /<DocsCallout\s+type="(\w+)"\s*>(.*?)<\/DocsCallout>/gs,
      type: "callout" as const,
    },
    {
      regex: /<DocsAccordion>(.*?)<\/DocsAccordion>/gs,
      type: "accordion" as const,
    },
    {
      regex: /<DocsStepByStep>([\s\S]*?)<\/DocsStepByStep>/gs,
      type: "stepbystep" as const,
    },
    {
      regex:
        /<DocsImageWithCaption\s+src="([^"]+)"\s+alt="([^"]+)"(?:\s+caption="([^"]*)")?\s*\/>/g,
      type: "image" as const,
    },
    {
      regex:
        /<DocsVideoEmbed\s+platform="(\w+)"\s+videoId="([^"]+)"(?:\s+title="([^"]*)")?\s*\/>/g,
      type: "video" as const,
    },
    {
      regex: /<DocsSlackButton\s*\/>/g,
      type: "slackbutton" as const,
    },
  ];

  const matches: Array<{
    index: number;
    length: number;
    type: string;
    match: RegExpMatchArray;
  }> = [];

  patterns.forEach(({ regex, type }) => {
    let match;
    regex.lastIndex = 0;
    while ((match = regex.exec(content)) !== null) {
      matches.push({
        index: match.index,
        length: match[0].length,
        type,
        match,
      });
    }
  });

  // Sort matches by index
  matches.sort((a, b) => a.index - b.index);

  matches.forEach(({ index, length, type, match }) => {
    // Add markdown before this component
    if (index > lastIndex) {
      const markdown = content.slice(lastIndex, index);
      if (markdown.trim()) {
        parts.push({ type: "markdown", content: markdown });
      }
    }

    // Add component
    if (type === "callout") {
      parts.push({
        type: "component",
        content: "callout",
        props: { type: match[1], children: match[2] },
      });
    } else if (type === "accordion") {
      const items = Array.from(
        match[1].matchAll(
          /<DocsAccordionItem\s+title="([^"]+)"\s*>(.*?)<\/DocsAccordionItem>/gs,
        ),
      );
      parts.push({
        type: "component",
        content: "accordion",
        props: { items: items.map((m) => ({ title: m[1], content: m[2] })) },
      });
    } else if (type === "stepbystep") {
      const steps = Array.from(
        match[1].matchAll(/<Step\s+title="([^"]+)"\s*>([\s\S]*?)<\/Step>/gs),
      );
      parts.push({
        type: "component",
        content: "stepbystep",
        props: {
          steps: steps.map((m) => ({
            title: m[1],
            content: m[2].trim(), // Trim whitespace from content
          })),
        },
      });
    } else if (type === "image") {
      parts.push({
        type: "component",
        content: "image",
        props: { src: match[1], alt: match[2], caption: match[3] },
      });
    } else if (type === "video") {
      parts.push({
        type: "component",
        content: "video",
        props: { platform: match[1], videoId: match[2], title: match[3] },
      });
    }

    lastIndex = index + length;
  });

  // Add remaining markdown
  if (lastIndex < content.length) {
    const markdown = content.slice(lastIndex);
    if (markdown.trim()) {
      parts.push({ type: "markdown", content: markdown });
    }
  }

  // If no components found, return entire content as markdown
  if (parts.length === 0) {
    return [{ type: "markdown", content }];
  }

  return parts;
}

const markdownComponents: any = {
  h1: ({ children }: any) => {
    const id = headingToId(String(children));
    return (
      <h1
        id={id}
        className="su-text-page-title mb-6 mt-8 first:mt-0 scroll-mt-20"
      >
        {children}
      </h1>
    );
  },
  h2: ({ children }: any) => {
    const id = headingToId(String(children));
    return (
      <h2
        id={id}
        className="su-text-section-heading mb-4 mt-8 first:mt-0 scroll-mt-20"
      >
        {children}
      </h2>
    );
  },
  h3: ({ children }: any) => {
    const id = headingToId(String(children));
    return (
      <h3
        id={id}
        className="text-lg font-semibold text-[var(--su-text-primary)] mb-3 mt-6 scroll-mt-20"
      >
        {children}
      </h3>
    );
  },
  h4: ({ children }: any) => {
    const id = headingToId(String(children));
    return (
      <h4
        id={id}
        className="text-base font-semibold text-[var(--su-text-primary)] mb-2 mt-4 scroll-mt-20"
      >
        {children}
      </h4>
    );
  },
  text: ({ children }: any) => {
    // Render text nodes as-is (not as code)
    return <>{children}</>;
  },
  p: ({ children }: any) => (
    <p className="text-[var(--su-text-secondary)] mb-4 leading-relaxed">
      {children}
    </p>
  ),
  strong: ({ children }: any) => (
    <strong className="font-semibold text-[var(--su-text-primary)]">
      {children}
    </strong>
  ),
  em: ({ children }: any) => (
    <em className="italic text-[var(--su-text-secondary)]">{children}</em>
  ),
  a: ({ href, children }: any) => {
    const isExternal = href?.startsWith("http") || href?.startsWith("mailto:");
    return (
      <a
        href={href}
        className="text-[var(--su-purple-accent)] hover:underline"
        target={isExternal ? "_blank" : undefined}
        rel={isExternal ? "noopener noreferrer" : undefined}
      >
        {children}
      </a>
    );
  },
  ul: ({ children }: any) => (
    <ul className="list-disc list-inside mb-4 space-y-2 text-[var(--su-text-secondary)]">
      {children}
    </ul>
  ),
  ol: ({ children }: any) => (
    <ol className="list-decimal list-inside mb-4 space-y-2 text-[var(--su-text-secondary)]">
      {children}
    </ol>
  ),
  li: ({ children }: any) => (
    <li className="text-[var(--su-text-secondary)]">{children}</li>
  ),
  code: ({ children, className: codeClassName }: any) => {
    const isInline = !codeClassName;
    return isInline ? (
      <code className="px-1.5 py-0.5 bg-[var(--su-bg-secondary)] border border-[var(--su-border-primary)] text-[var(--su-purple-accent)] text-sm rounded-none">
        {children}
      </code>
    ) : (
      <code className={codeClassName}>{children}</code>
    );
  },
  pre: ({ children }: any) => (
    <pre className="mb-4 p-4 bg-[var(--su-bg-secondary)] border border-[var(--su-border-primary)] overflow-x-auto rounded-none">
      {children}
    </pre>
  ),
  blockquote: ({ children }: any) => (
    <blockquote className="border-l-4 border-[var(--su-purple-accent)] pl-4 my-4 italic text-[var(--su-text-muted)]">
      {children}
    </blockquote>
  ),
  table: ({ children }: any) => (
    <div className="overflow-x-auto my-6">
      <table className="min-w-full border-collapse border border-[var(--su-border-primary)]">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }: any) => (
    <thead className="bg-[var(--su-bg-secondary)]">{children}</thead>
  ),
  tbody: ({ children }: any) => <tbody>{children}</tbody>,
  tr: ({ children }: any) => (
    <tr className="border-b border-[var(--su-border-primary)]">{children}</tr>
  ),
  th: ({ children }: any) => (
    <th className="px-4 py-2 text-left font-semibold text-[var(--su-text-primary)] border border-[var(--su-border-primary)]">
      {children}
    </th>
  ),
  td: ({ children }: any) => (
    <td className="px-4 py-2 text-[var(--su-text-secondary)] border border-[var(--su-border-primary)]">
      {children}
    </td>
  ),
  hr: () => <hr className="my-8 border-[var(--su-border-primary)]" />,
  img: ({ src, alt, ...props }: any) => {
    // Check if this is the Slack button
    if (src?.includes("add_to_slack")) {
      return (
        <a
          href="https://slack.com/oauth/v2/authorize?client_id=8228472016453.9830655217430&scope=channels:history,channels:read,groups:history,groups:read,team:read,channels:join&user_scope=users:read,usergroups:read"
          className="inline-block my-6 hover:opacity-80 transition-opacity"
        >
          <img
            src={src}
            alt={alt || "Add to Slack"}
            height="40"
            width="139"
            srcSet="https://platform.slack-edge.com/img/add_to_slack.png 1x, https://platform.slack-edge.com/img/add_to_slack@2x.png 2x"
            {...props}
          />
        </a>
      );
    }
    return <DocsImageWithCaption src={src || ""} alt={alt || ""} />;
  },
  div: ({ children, className, ...props }: any) => {
    // Allow divs with className to pass through (for custom styling)
    if (className) {
      return (
        <div className={className} {...props}>
          {children}
        </div>
      );
    }
    return <div {...props}>{children}</div>;
  },
};

function renderComponent(
  type: string,
  props: any,
  key: number,
): React.ReactNode {
  switch (type) {
    case "callout":
      return (
        <DocsCallout key={key} type={props.type}>
          {props.children}
        </DocsCallout>
      );
    case "accordion":
      return (
        <DocsAccordion key={key}>
          {props.items.map((item: any, idx: number) => (
            <DocsAccordionItem key={idx} title={item.title}>
              {item.content}
            </DocsAccordionItem>
          ))}
        </DocsAccordion>
      );
    case "stepbystep":
      return (
        <DocsStepByStep key={key}>
          {props.steps.map((step: any, idx: number) => (
            <Step key={idx} title={step.title}>
              {step.content.trim()}
            </Step>
          ))}
        </DocsStepByStep>
      );
    case "image":
      return (
        <DocsImageWithCaption
          key={key}
          src={props.src}
          alt={props.alt}
          caption={props.caption}
        />
      );
    case "video":
      return (
        <DocsVideoEmbed
          key={key}
          platform={props.platform}
          videoId={props.videoId}
          title={props.title}
        />
      );
    case "slackbutton":
      return <DocsSlackButton key={key} />;
    default:
      return null;
  }
}

export function MarkdownRenderer({
  content,
  className,
}: MarkdownRendererProps) {
  const parts = splitByCustomComponents(content);

  return (
    <div className={cn("prose prose-invert max-w-none", className)}>
      {parts.map((part, index) => {
        if (part.type === "markdown") {
          return (
            <ReactMarkdown
              key={index}
              components={markdownComponents}
              rehypePlugins={[rehypeRaw]}
            >
              {part.content}
            </ReactMarkdown>
          );
        } else {
          return renderComponent(part.content, part.props, index);
        }
      })}
    </div>
  );
}
